"use client";

import React from "react";
import Link from "next/link";
import SafeHtml from "@/components/ui/SafeHtml";
import type { ACFNestedBase, LinkField } from "@/types/acf";

type Card = {
  title?: string;
  text?: string;
  link?: LinkField | false;
};

export interface CardLinksFields extends ACFNestedBase {
  hide?: boolean;
  cards?: Card[];
}

export default function CardLinks({ data }: { data: CardLinksFields }) {
  if (!data || data.hide) return null;

  const cards = Array.isArray(data.cards) ? data.cards : [];
  if (cards.length === 0) return null;

  const isOdd = cards.length % 2 === 1;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {cards.map((card, index) => {
        const link =
          card.link && typeof card.link === "object" && card.link.url
            ? card.link
            : null;

        const hasLink = !!link;
        const spanFull = isOdd && index === cards.length - 1;
        const wrapperClass = spanFull ? "sm:col-span-2" : "";

        const inner = (
          <div
            className={[
              "relative h-full px-4 pt-5 flex flex-col",
              hasLink ? "pb-12" : "pb-5",
              "bg-fuji-green text-black",
              hasLink
                ? "group cursor-pointer transition-colors duration-200 hover:bg-fuji-black hover:text-white"
                : "cursor-default",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {card.title ? <h3 className="text-title-3 m-0">{card.title}</h3> : null}

            {card.text ? (
              <div className="mt-3 body-text min-w-0 break-words">
                <SafeHtml content={card.text} />
              </div>
            ) : null}

            {hasLink ? (
              <span
                className="
                  absolute bottom-4 left-4
                  inline-flex h-[19px] w-[18px] items-center justify-center
                  [&>svg]:h-[19px] [&>svg]:w-[18px]
                  transform-gpu transition-transform duration-300
                  [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]
                  group-hover:translate-x-1
                "
                aria-hidden="true"
              >
                <svg
                  className="stroke-current"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="13"
                  viewBox="0 0 18 13"
                  fill="none"
                  focusable="false"
                >
                  <path
                    d="M1 6.5H17M17 6.5L11.4998 1M17 6.5L11.4998 12"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            ) : null}
          </div>
        );

        if (!hasLink || !link) {
          return (
            <div key={index} className={wrapperClass}>
              {inner}
            </div>
          );
        }

        return (
          <Link
            key={index}
            href={link.url}
            target={link.target || undefined}
            rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
            className={`no-underline ${wrapperClass}`}
          >
            {inner}
          </Link>
        );
      })}
    </div>
  );
}
